import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    Output,
    ViewChild
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import {
    AirspaceCheckerParametersWithStartEnd,
    MapAirspaceCheckerService
} from '@flyfreely-portal-ui/airspace';
import { CommonDialoguesService } from '@flyfreely-portal-ui/common-dialogues';
import {
    AirspaceJurisdictionDto,
    CreateLocationCommand,
    DO_NOTHING,
    DateFormatService,
    DateRange,
    FEATURE_LOTAM,
    FlyFreelyError,
    FlyFreelyLoggingService,
    GeospatialService,
    LocationDetailsDto,
    LocationDto,
    LocationFeatureDto,
    LocationService,
    OperationsSummaryService,
    OrganisationService,
    PersonsOrganisationDto,
    PreferencesService,
    UpdateLocationCommand,
    WorkTracker,
    browserTimezone,
    hasFeatureFlag,
    toTimestamp
} from '@flyfreely-portal-ui/flyfreely';
import { FullScreenService } from '@flyfreely-portal-ui/fullscreen';
import {
    FeatureAndGroup,
    FeatureGroup,
    MapFeature,
    MapViewport
} from '@flyfreely-portal-ui/map';
import { WorkspaceStateService } from '@flyfreely-portal-ui/workspace';
import booleanValid from '@turf/boolean-valid';
import center from '@turf/center';
import { Angulartics2 } from 'angulartics2';
import * as FileSaver from 'file-saver';
import { getOrElse, map } from 'fp-ts/es6/Option';
import { pipe } from 'fp-ts/es6/function';
import {
    FeatureCollection,
    GeoJsonProperties,
    GeometryCollection,
    LineString
} from 'geojson';
import { AirspaceCheckService } from 'libs/airspace/src/lib/airspace-check/airspace-check.service';
import { AIRSPACE_DISCLAIMER } from 'libs/locations/src/lib/interfaces';
import { buildAreaLineFeature } from 'libs/map/src/lib/draw-modes/draw-area-line';
import { FlyFreelyMapComponent } from 'libs/map/src/lib/flyfreely-map/flyfreely-map.component';
import {
    missionLocationsToFeatureCollection,
    noticeLocationsToFeatureCollection
} from 'libs/missions/src/lib/helpers';
import {
    MissionNoticesDataService,
    SimplifiedLotamDetails
} from 'libs/notices/src/lib/mission-notices-data.service';
import moment from 'moment';
import { BsModalRef, BsModalService, ModalOptions } from 'ngx-bootstrap/modal';
import { BehaviorSubject, Subject, combineLatest, of } from 'rxjs';
import {
    debounceTime,
    distinctUntilChanged,
    filter,
    map as subjectMap,
    switchMap,
    take,
    takeUntil,
    tap
} from 'rxjs/operators';
import {
    flightAreaOfFeatures,
    getFeatureGroups,
    getFileName,
    toMapFeature
} from '../helpers';
import {
    KmlFeatureGroups,
    KmlUploadService,
    UploadedFeature,
    isSinglePolygon
} from '../kml-upload.service';
import { showKmlUploadDialogue } from '../kml-upload/kml-upload-dialogue.component';
import { LocationEditService } from '../location-edit.service';
import { UgcsUploadService } from '../ugcs-upload.service';

// Mapbox requires a non-negative value for IDs
const NEW_FEATURE = 0;

// This mirrors the InitialCheckerParameters in the airspace library.
export interface MissionAirspaceParameters {
    id: number;
    date: Date;
    duration: number;
    rulesetIdentifier: string;
    height: number;
}

@Component({
    selector: 'location-edit-v2-dialogue',
    templateUrl: './location-edit-dialogue.component.html',
    styleUrls: ['./location-edit-dialogue.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        AirspaceCheckService,
        MapAirspaceCheckerService,
        LocationEditService,
        MissionNoticesDataService,
        UgcsUploadService
    ]
})
export class LocationEditV2Dialogue {
    @Input() locationId: number;
    @Input() organisationId: number;
    @Input() type: LocationDetailsDto.Type | LocationDto.Type =
        LocationDto.Type.TEMPLATE;
    @Input() missionValues: MissionAirspaceParameters;
    /**
     * These are the default values used to pre-populate a new location.
     * These are only used for new locations.
     */
    @Input() defaultValues: Partial<LocationDetailsDto>;
    @Input() canCreateMission: boolean;
    @Input() showAirspaceFlag: boolean;
    @Input() showLocationName: boolean;
    @Input() hideFlightArea: boolean;
    @Input() title = '';
    @Input() manualSave: boolean;
    @Input() lotamDetails?: SimplifiedLotamDetails;
    @Input() allowKmlDownload: boolean = true;

    @Output() change = new EventEmitter<LocationDetailsDto>();
    @Output() featuresChange = new EventEmitter<FeatureGroup[]>();
    @Output() updatedMissionValues =
        new EventEmitter<MissionAirspaceParameters>();

    mapReady = false;

    organisation: PersonsOrganisationDto;
    location: LocationDetailsDto;
    downloadUrl: string;

    featureGroups: FeatureGroup[];
    invalidFeatures: Set<string> = new Set<string>();

    canUseLotams$ = new BehaviorSubject<boolean>(false);

    mapData$ = new BehaviorSubject<{
        [source: string]: FeatureCollection;
    }>({});
    mapCenter$ = new BehaviorSubject<MapViewport>(null);
    timeZoneSource:
        | 'ORGANISATION'
        | 'MAP_CENTER'
        | 'FLIGHT_AREA'
        | 'AIRSPACE_CHECK' = 'ORGANISATION';
    currentTimeZone: string;

    @ViewChild('map', { static: false }) map: FlyFreelyMapComponent;
    @ViewChild('drawingArea', { static: true }) drawingArea: ElementRef;

    formGroup: FormGroup;
    private featuresUpdated$ = new Subject<boolean>();
    private ngUnsubscribe$ = new Subject<void>();

    workTracker = new WorkTracker();

    isAddingFeature = false;
    working = false;

    hasRequiredFeatures = false;
    hasAnyFeatures = false;

    hoverFeatureId: number;
    editFeatureId: number;
    highlightFeatureId: number;

    isEditing = false;

    /**
     * The current values in the airspace checker. This is managed by the location-airspace-check component
     */
    airspaceValue$ = new BehaviorSubject<AirspaceCheckerParametersWithStartEnd>(
        {
            airspaceDate: null,
            airspaceDuration: 0,
            airspaceHeight: 0,
            airspaceRuleset: null,
            startTime: null,
            endTime: null,
            airspaceTimeZone: null
        }
    );

    private hasChanged = false;
    private wasNameSetByUser = false;

    private nextId = 1;

    selectedFeature: FeatureAndGroup;
    addedFeature: FeatureAndGroup;
    selectedFeatureGroup: FeatureGroup;

    // Airspace Disclaimer
    showAirspaceDisclaimer = false;
    shouldShowAirspaceDisclaimer$ = new Subject<void>();
    airspaceDisclaimerDates: { [key: string]: Date } = {};
    mapJurisdiction: AirspaceJurisdictionDto;

    flightArea: GeoJSON.Polygon;

    private isLoaded = false;

    constructor(
        private modal: BsModalRef<LocationEditV2Dialogue>,
        modalOptions: ModalOptions,
        private modalService: BsModalService,
        private locationService: LocationService,
        private kmlUploadService: KmlUploadService,
        private workspaceStateService: WorkspaceStateService,
        private organisationService: OrganisationService,
        private locationEditService: LocationEditService,
        private preferencesService: PreferencesService,
        private geospatialService: GeospatialService,
        private airspaceCheckService: AirspaceCheckService,
        private missionNoticesDataService: MissionNoticesDataService,
        private commonDialoguesService: CommonDialoguesService,
        private fullscreenService: FullScreenService,
        private operationsSummaryService: OperationsSummaryService,
        private logging: FlyFreelyLoggingService,
        private changeDetector: ChangeDetectorRef,
        private angulartics2: Angulartics2,
        private ugcsUploadService: UgcsUploadService,
        private dateFormatService: DateFormatService
    ) {
        modalOptions.closeInterceptor = () => {
            this.handleEditOnClose();

            if (this.hasChanged || this.formGroup.dirty) {
                return this.commonDialoguesService.showConfirmationDialogue(
                    'Confirm Cancel',
                    `You have unsaved changes, are you sure you want to cancel?`,
                    'Yes',
                    () => Promise.resolve()
                );
            }
            return Promise.resolve();
        };

        this.formGroup = new FormGroup({
            name: new FormControl(undefined, [Validators.required])
        });

        combineLatest([
            this.workTracker.observable,
            this.locationEditService.working$
        ])
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(([working, serviceWorking]) => {
                this.working = working || serviceWorking;
                this.changeDetector.markForCheck();
            });

        this.shouldShowAirspaceDisclaimer$
            .pipe(debounceTime(500), takeUntil(this.ngUnsubscribe$))
            .subscribe(() => this.showAirspaceDisclaimerDialogue());

        // Start the time zone used with the organisation default, falling back on the browser time zone.
        this.workspaceStateService.currentLoadedOrganisation$
            .pipe(
                filter(org => org != null),
                take(1)
            )
            .subscribe(
                organisation =>
                    (this.currentTimeZone =
                        organisation.organisation.timeZone ?? browserTimezone)
            );
    }

    ngOnInit() {
        combineLatest([
            this.workTracker.observable,
            this.locationEditService.working$
        ])
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(([working, serviceWorking]) => {
                this.working = working || serviceWorking;
                this.changeDetector.markForCheck();
            });

        this.formGroup.controls.name.valueChanges
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(newValue => this.onFormChanges(newValue));
        this.refreshLocation();

        this.featuresUpdated$
            .pipe(
                // Debounce to allow the map to render the new features
                debounceTime(300),
                takeUntil(this.ngUnsubscribe$)
            )
            .subscribe(updated => {
                if (!this.isLoaded) {
                    this.isLoaded = this.map.zoomToAllFeatures(updated);
                } else {
                    this.map.zoomToAllFeatures(updated);
                }
            });

        this.locationEditService.missionCreated$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(() => {
                this.hasChanged = false;
                this.formGroup.markAsPristine();
                this.modal.hide();
            });

        this.refreshPermissions();
        if (this.type === LocationDto.Type.MISSION) {
            // Setup to keep LOTAM up to date
            combineLatest([this.canUseLotams$, this.airspaceValue$]).subscribe(
                ([canUseLotams, airspaceValue]) => {
                    if (canUseLotams) {
                        const dateRange: DateRange = {
                            start: airspaceValue.startTime,
                            end: airspaceValue.endTime
                        };
                        this.missionNoticesDataService.refreshNotices(
                            dateRange,
                            this.organisationId
                        );
                    } else {
                        this.missionNoticesDataService.resetNotices();
                    }
                }
            );

            combineLatest([
                this.missionNoticesDataService.lotams$,
                this.airspaceValue$
            ])
                .pipe(
                    // This will fire several times as the map initialises, so debounce to limit network calls
                    debounceTime(500),
                    distinctUntilChanged(),
                    switchMap(([notices, airspaceValues]) => {
                        const effectiveTimeZone =
                            airspaceValues.airspaceTimeZone ??
                            this.currentTimeZone ??
                            browserTimezone;
                        const now = moment(this.missionValues.date).tz(
                            effectiveTimeZone
                        );
                        const nowEnd = moment(now).endOf('day').toISOString();
                        const startTime =
                            airspaceValues?.airspaceDate != null
                                ? toTimestamp(
                                      moment(airspaceValues.airspaceDate)
                                          .tz(effectiveTimeZone)
                                          .startOf('day')
                                          .toDate(),
                                      airspaceValues.airspaceTimeZone
                                  )
                                : now.toISOString();
                        const endTime =
                            airspaceValues?.airspaceDate != null
                                ? toTimestamp(
                                      moment
                                          .tz(
                                              airspaceValues.airspaceDate,
                                              airspaceValues.airspaceTimeZone
                                          )
                                          .endOf('day')
                                          .toDate(),
                                      airspaceValues.airspaceTimeZone
                                  )
                                : nowEnd;
                        return combineLatest([
                            of(notices),
                            this.operationsSummaryService.findMissionLocations(
                                {
                                    organisationId: this.organisationId,
                                    startTime,
                                    endTime
                                },
                                []
                            )
                        ]);
                    })
                )
                .pipe(takeUntil(this.ngUnsubscribe$))
                .subscribe(([notices, missionLocationList]) => {
                    const [pointLocations, polygonLocations] =
                        missionLocationsToFeatureCollection(
                            missionLocationList.map(missionLocation => ({
                                ...missionLocation,
                                // Filter the current mission from the locations, but not the current location
                                // This means that overlapping missions using the same location ID will still show if their times overlap.
                                missionList: missionLocation.missionList.filter(
                                    mission =>
                                        this.missionValues == null ||
                                        mission.id !== this.missionValues.id
                                )
                            })),
                            this.dateFormatService
                        );
                    this.mapData$.next({
                        LOTAM: noticeLocationsToFeatureCollection(
                            notices,
                            this.organisation.timeZone
                        ),
                        MISSIONS: polygonLocations,
                        MISSION_MARKERS: pointLocations
                    });
                });
        }

        if (this.lotamDetails != null) {
            this.missionNoticesDataService.lotams$
                .pipe(takeUntil(this.ngUnsubscribe$))
                .subscribe(notices => {
                    // Filter out the notice currently being edited
                    const filteredNotices = notices.filter(
                        notice => notice.id !== this.lotamDetails.lotamId
                    );
                    this.mapData$.next({
                        LOTAM: noticeLocationsToFeatureCollection(
                            filteredNotices,
                            this.organisation.timeZone
                        )
                    });
                });

            this.missionNoticesDataService.refreshNotices(
                this.lotamDetails.dateRange,
                this.organisationId
            );
        }
        this.setupTimeZoneHandlers();

        this.refreshPermissions();
    }

    ngOnDestroy() {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
        this.featuresUpdated$.complete();
        this.mapData$.complete();
        this.shouldShowAirspaceDisclaimer$.complete();
        this.mapCenter$.complete();
        this.canUseLotams$.complete();
        this.airspaceValue$.complete();
    }

    @HostListener('document:keyup', ['$event'])
    handleDeleteKeyboardEvent(event: KeyboardEvent) {
        if (
            event.key === 'Delete' &&
            this.selectedFeatureGroup &&
            this.selectedFeature
        ) {
            this.deleteFeature(
                this.selectedFeatureGroup,
                this.selectedFeature.feature
            );
        }
    }

    private refreshLocation() {
        if (this.locationId != null) {
            this.locationService
                .findLocation(this.locationId, this.organisationId)
                .pipe(takeUntil(this.ngUnsubscribe$))
                .subscribe(location => this.setLocation(location))
                .add(this.workTracker.createTracker());
        } else {
            if (this.defaultValues != null) {
                this.formGroup.patchValue({
                    name: this.defaultValues.name
                });

                this.setFeatures(this.defaultValues.features ?? []);
            } else {
                this.setFeatures([]);
            }
        }
    }

    refreshPermissions() {
        this.organisationService
            .findByIdForUser(this.organisationId, this.organisationId)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(organisation => {
                this.organisation = organisation;
                this.canUseLotams$.next(
                    hasFeatureFlag(organisation, FEATURE_LOTAM)
                );
            });
    }

    /**
     * To ensure the map has the most accurate time zone as the user navigates and plans
     * these handlers update the time zone used based on the most authoritative source.
     */
    private setupTimeZoneHandlers() {
        this.mapCenter$
            .pipe(
                filter(viewport => viewport != null),
                debounceTime(1000),
                switchMap(viewport =>
                    this.geospatialService.findTimeZone(
                        viewport.latitude,
                        viewport.longitude
                    )
                ),
                takeUntil(this.ngUnsubscribe$)
            )
            .subscribe({
                next: timezone => {
                    this.currentTimeZone =
                        timezone?.timeZone != null
                            ? timezone.timeZone
                            : this.currentTimeZone;
                    this.changeDetector.markForCheck();
                },
                // The errors here normally spring from the backend due to issues with coordinates
                // We can ignore them and the timezone will correct itself the next time the map moves,
                // or once the user starts planning
                error: (error: FlyFreelyError) => DO_NOTHING
            });

        this.airspaceCheckService.result$
            .pipe(
                filter(
                    check =>
                        check != null &&
                        this.timeZoneSource !== 'AIRSPACE_CHECK'
                ),
                takeUntil(this.ngUnsubscribe$)
            )
            .subscribe(result => {
                this.timeZoneSource = 'AIRSPACE_CHECK';
                this.currentTimeZone = result.timeZone;
                this.changeDetector.detectChanges();
            });
    }

    edit() {
        if (this.featureGroups[0].existingFeatures.length === 0) {
            this.map.editFeature({
                groupId: this.featureGroups[0].id,
                feature: null
            });
        } else {
            this.map.editFeature({
                groupId: this.featureGroups[0].id,
                feature: this.featureGroups[0].existingFeatures[0]
            });
        }
    }

    onFeatureNameChange(featureGroup: FeatureGroup, feature: MapFeature) {
        this.map.showFeature(
            {
                feature,
                groupId: featureGroup.id
            },
            true
        );
    }

    onSelectedFeatureUpdated(updated: FeatureAndGroup) {
        // for some reason, updated.feature.categoryId would still be the old value
        // however, when debugging and logging the updated object, it would show the new value.
        // tried many things, only option found was this ugly setTimeout
        window.setTimeout(() => {
            if (
                updated.feature.categoryId ===
                    LocationFeatureDto.Type.OFFSET_FLIGHT_AREA ||
                updated.feature.categoryId ===
                    LocationFeatureDto.Type.OFFSET_FLIGHT_AREA ||
                updated.feature.categoryId ===
                    LocationFeatureDto.Type.OFFSET_NO_FLY_AREA
            ) {
                this.handleOffsetFeatureChanged(updated);
            }

            this.setFeatureName(updated);
            this.setSelectedFeature(updated);
            this.onFeaturesUpdated(updated);
            this.changeDetector.detectChanges();
        });
    }

    onMapReady() {
        if (!this.mapReady) {
            this.mapReady = true;
            this.featuresUpdated$.next(true);
        }
    }

    onMapJurisdictionChanged(jurisdiction: AirspaceJurisdictionDto) {
        this.mapJurisdiction = jurisdiction;
        this.showAirspaceDisclaimer = false;
        this.shouldShowAirspaceDisclaimer$.next();
    }

    onAirspaceValuesUpdated(values: AirspaceCheckerParametersWithStartEnd) {
        this.airspaceValue$.next(values);
    }

    onFeaturesUpdated(updated: FeatureAndGroup) {
        if (this.isAddingFeature) {
            return;
        }
        this.hasChanged = true;

        const isNew = updated.feature.id == null;

        const feature = !isNew
            ? updated
            : {
                  groupId: updated.groupId,
                  feature: {
                      ...updated.feature,
                      id: NEW_FEATURE,
                      name: this.formGroup.value.name
                  }
              };

        if (feature?.feature.children != null) {
            feature.feature.children.forEach(c => {
                c.categoryId = feature.feature.categoryId;
            });
        }

        const updateFeature = (existing: MapFeature[], toAdd: MapFeature) =>
            !isNew
                ? existing.map(f => (f.id === toAdd.id ? toAdd : f))
                : existing.concat(toAdd);

        this.featureGroups = this.featureGroups.map(g =>
            g.id === updated.groupId
                ? {
                      ...g,
                      existingFeatures: updateFeature(
                          g.existingFeatures,
                          feature.feature
                      )
                  }
                : g
        );

        this.refreshValidity();

        if (feature.feature.id === NEW_FEATURE) {
            this.map.showFeature(feature);
        }
    }

    onFeatureSelected(selectedFeature: FeatureAndGroup) {
        if (this.isAddingFeature) {
            return;
        }

        if (
            this.selectedFeature != null &&
            this.selectedFeature.feature.id === selectedFeature.feature.id
        ) {
            this.onFeatureEdit(selectedFeature);
        } else {
            this.setSelectedFeature(selectedFeature);
        }
    }

    onFeatureUnselected() {
        if (!this.isAddingFeature) {
            this.selectedFeature = null;
            this.selectedFeatureGroup = null;
        }
    }

    onFeatureEdit(feature: FeatureAndGroup) {
        this.map.editFeature(
            {
                feature: feature.feature,
                groupId: feature.groupId
            },
            this.findDrawProperties(feature)
        );
        this.setSelectedFeature(feature);
    }

    onFeatureDelete(feature: FeatureAndGroup) {
        const featureGroup = this.featureGroups.find(
            fg => fg.id === feature.groupId
        );
        this.deleteFeature(featureGroup, feature.feature);
        this.setSelectedFeature(null);
        // Lock the save button if no longer valid after delete
        this.refreshValidity();
        this.map.hidePopup();
    }

    onGeocodeResultSelected(feature: MapFeature) {
        if (!this.wasNameSetByUser) {
            this.formGroup.patchValue(
                { name: feature.name },
                { emitEvent: false }
            );
        }
    }

    onFormChanges(newValue: { name: string }) {
        this.wasNameSetByUser = true;
    }

    onFullscreenRequested() {
        this.fullscreenService.toggleFullScreen(this.drawingArea);
    }

    onEditModeChanged(isEditingOrEvent: boolean | string) {
        if (isEditingOrEvent === 'cancelled') {
            this.handlePolygonDrawingCancelled();
            return;
        }

        this.hasChanged = true;

        if (this.isEditing && this.isAddingFeature && !isEditingOrEvent) {
            this.isAddingFeature = false;
            this.refreshValidity();
            this.map.showPopup();
            this.setSelectedFeature(this.addedFeature);
        }

        if (!isEditingOrEvent) {
            this.validateFeatures();
        }

        this.isEditing = isEditingOrEvent as boolean;

        this.changeDetector.detectChanges();
    }

    handlePolygonDrawingCancelled() {
        if (this.isAddingFeature && this.addedFeature) {
            this.cancelAddingFeature();
            this.isAddingFeature = false;
            this.logging.info(
                'Polygon drawing cancelled - at least 3 points are required to create a polygon'
            );
            this.setSelectedFeature(null);
            this.isEditing = false;
            this.changeDetector.detectChanges();
        }
    }

    onFeatureHover(featureId: number) {
        this.hoverFeatureId = featureId;
    }

    onFeaturesLoaded() {
        this.featuresUpdated$.next(false);
    }

    onFeatureNameSelected(feature: MapFeature) {
        if (this.showAirspaceDisclaimer) {
            return;
        }
        const groupId = this.featureGroups.find(
            g => g.existingFeatures.filter(f => f.id === feature.id).length > 0
        ).id;
        const featureAndGroup: FeatureAndGroup = {
            groupId,
            feature
        };
        this.setSelectedFeature(featureAndGroup);
        this.map.zoomToFeature(feature, true, false);
    }

    private handleOffsetFeatureChanged(featureAndGroup: FeatureAndGroup) {
        const linestring = (<GeometryCollection>(
            featureAndGroup.feature.geom
        )).geometries.find(g => g.type === 'LineString');
        const updatedFeature = buildAreaLineFeature(
            <LineString>linestring,
            featureAndGroup.feature.properties
        );
        featureAndGroup.feature.geom = updatedFeature.geometry;
        featureAndGroup.feature.properties = updatedFeature.properties;
    }

    private refreshValidity() {
        const mergeAcc = (
            a: { hasAnyFeatures: boolean; hasRequiredFeatures: boolean },
            b: { hasAnyFeatures: boolean; hasRequiredFeatures: boolean }
        ) => ({
            hasAnyFeatures: a.hasAnyFeatures || b.hasAnyFeatures,
            hasRequiredFeatures: a.hasRequiredFeatures || b.hasRequiredFeatures
        });

        const { hasAnyFeatures, hasRequiredFeatures } =
            this.featureGroups.reduce(
                (acc, fg) =>
                    mergeAcc(
                        acc,
                        fg.existingFeatures.reduce(
                            (acc2, f) => ({
                                hasAnyFeatures: true,
                                hasRequiredFeatures:
                                    acc2.hasRequiredFeatures ||
                                    f.categoryId === 'FLIGHT_AREA' ||
                                    f.categoryId === 'OFFSET_FLIGHT_AREA' ||
                                    this.manualSave
                            }),
                            {
                                hasAnyFeatures: false,
                                hasRequiredFeatures: false
                            } as {
                                hasAnyFeatures: boolean;
                                hasRequiredFeatures: boolean;
                            }
                        )
                    ),
                { hasAnyFeatures: false, hasRequiredFeatures: false }
            );

        this.hasAnyFeatures = hasAnyFeatures;
        this.hasRequiredFeatures = hasRequiredFeatures;

        this.flightArea = pipe(
            flightAreaOfFeatures(this.getFeatures()),
            map<LocationFeatureDto, GeoJSON.Polygon>(f =>
                f.geometry.type !== 'GeometryCollection'
                    ? <GeoJSON.Polygon>f.geometry
                    : <GeoJSON.Polygon>(
                          (<GeometryCollection>f.geometry).geometries.find(
                              g => g.type === 'Polygon'
                          )
                      )
            ),
            getOrElse(() => null)
        );
        this.shouldShowAirspaceDisclaimer$.next();

        // Handle time zone source
        if (
            this.hasRequiredFeatures &&
            this.flightArea != null &&
            (this.timeZoneSource === 'ORGANISATION' ||
                this.timeZoneSource === 'MAP_CENTER')
        ) {
            this.timeZoneSource = 'FLIGHT_AREA';
            const flightAreaCenter = center(this.flightArea);
            this.mapCenter$.next({
                latitude: flightAreaCenter.geometry.coordinates[1],
                longitude: flightAreaCenter.geometry.coordinates[0],
                zoom: null
            });
        } else {
            // Reset the timezone source
            this.timeZoneSource =
                this.timeZoneSource === 'ORGANISATION' ||
                this.timeZoneSource === 'MAP_CENTER'
                    ? this.timeZoneSource
                    : (this.timeZoneSource = 'MAP_CENTER');
        }
    }

    private setLocation(location: LocationDetailsDto) {
        this.formGroup.patchValue(
            { name: location.name },
            { emitEvent: false }
        );
        this.wasNameSetByUser = true;

        this.location = location;

        this.setFeatures(location.features);
    }

    private setFeatures(locationFeatures: LocationFeatureDto[]) {
        const { features, nextId } = getFeatureGroups(
            locationFeatures,
            ++this.nextId,
            0,
            this.hideFlightArea
        );
        this.featureGroups = features;
        this.nextId = nextId + 1;
        this.validateFeatures();
        this.refreshValidity();
    }

    private addFeature(locationFeature: LocationFeatureDto) {
        this.featureGroups = this.featureGroups.map(fg =>
            fg.categories.findIndex(c => c.id === locationFeature.type) !== -1
                ? {
                      ...fg,
                      existingFeatures: fg.existingFeatures.concat(
                          toMapFeature(locationFeature, this.nextId++)
                      )
                  }
                : fg
        );
        this.refreshValidity();
    }

    validateFeatures() {
        this.invalidFeatures.clear();
        this.featureGroups.forEach(group => {
            if (group.name === 'Corridors') {
                return;
            }
            group.existingFeatures.forEach(feature => {
                const featureKey = `${group.id}-${feature.id}`;

                if (!this.isFeatureValid(feature)) {
                    this.invalidFeatures.add(featureKey);
                }
            });
        });
        this.changeDetector.detectChanges();
    }

    private isFeatureValid(feature: MapFeature): boolean {
        try {
            if (!feature.geom) {
                return false;
            }
            const geoJsonFeature = {
                type: 'Feature' as const,
                geometry: feature.geom,
                properties: feature.properties || {}
            };
            return booleanValid(geoJsonFeature);
        } catch (error) {
            return false;
        }
    }

    isFeatureInvalid(groupId: number, featureId: number | string): boolean {
        return this.invalidFeatures.has(`${groupId}-${featureId}`);
    }

    hasInvalidFeatures(): boolean {
        return this.invalidFeatures.size > 0;
    }

    uploadLocation(locationFiles: File) {
        const locationFile = locationFiles;

        if (
            locationFile.type === 'application/json' ||
            locationFile.name.toLowerCase().endsWith('.json')
        ) {
            this.uploadUgCSJSON(locationFile);
            return;
        }

        this.locationService
            .uploadLocation(locationFile)
            .pipe(
                takeUntil(this.ngUnsubscribe$),
                take(1),
                tap({
                    next: DO_NOTHING,
                    error: (error: FlyFreelyError) =>
                        this.logging.error(
                            error,
                            `Error uploading KML file: ${error.message}`
                        )
                })
            )
            .subscribe(featureCollection => {
                const features =
                    this.kmlUploadService.parseKmlGroupings(featureCollection);
                if (features.length === 0) {
                    this.logging.error(
                        null,
                        `The uploaded KML does not contain any valid location geometry.`
                    );
                } else if (isSinglePolygon(features)) {
                    this.parseSingleKmlFeature(
                        features[0].features[0],
                        locationFile.name
                    );
                } else {
                    showKmlUploadDialogue(
                        this.modalService,
                        features,
                        locationFile.name
                    )
                        .content.selectedFeatures.pipe(
                            takeUntil(this.ngUnsubscribe$)
                        )
                        .subscribe(features =>
                            this.parseComplexKml(features, locationFile.name)
                        );
                }
            })
            .add(this.workTracker.createTracker());
    }

    parseSingleKmlFeature(feature: UploadedFeature, fileName: string) {
        this.addFeature({
            name: fileName,
            type: feature.featureType,
            geometry: <GeoJSON.Polygon>feature.geometry
        } as LocationFeatureDto);
        this.refreshValidity();
        this.changeDetector.markForCheck();
        this.featuresUpdated$.next(false);

        if (!this.wasNameSetByUser) {
            this.formGroup.patchValue(
                { name: feature.name },
                { emitEvent: false }
            );
        }

        this.angulartics2.eventTrack.next({
            action: 'kml-single-feature-import',
            properties: {
                category: `kml-upload`
            }
        });
    }

    parseComplexKml(features: KmlFeatureGroups[], name: string) {
        features.forEach(group => {
            group.features.forEach(f => {
                this.addFeature({
                    name: f.name,
                    type: f.featureType,
                    geometry: f.geometry
                } as LocationFeatureDto);
            });
        });
        this.refreshValidity();
        this.changeDetector.markForCheck();
        this.featuresUpdated$.next(false);

        if (!this.wasNameSetByUser) {
            this.formGroup.patchValue({ name: name }, { emitEvent: false });
        }
    }

    uploadUgCSJSON(locationFile: File) {
        const reader = new FileReader();

        reader.onloadend = e => {
            try {
                if (e.target?.readyState === FileReader.DONE) {
                    const jsonString = e.target?.result as string;

                    const validationResult =
                        this.ugcsUploadService.validateAndParseJSON(jsonString);

                    const locationFeatures =
                        this.ugcsUploadService.convertToLocationFeatures(
                            validationResult.data
                        );

                    const currentLocationName =
                        this.formGroup.get('name')?.value;
                    if (
                        currentLocationName == null ||
                        currentLocationName.trim() === ''
                    ) {
                        const missionName =
                            this.ugcsUploadService.getMissionName(
                                validationResult.data
                            );
                        if (missionName) {
                            this.formGroup.patchValue(
                                { name: missionName },
                                { emitEvent: false }
                            );
                        }
                    }

                    locationFeatures.forEach(feature => {
                        this.addFeature(feature);
                    });

                    this.changeDetector.markForCheck();
                    this.featuresUpdated$.next(false);

                    this.logging.success(
                        `Successfully imported ${locationFeatures.length} features from UgCS mission plan`
                    );
                }
            } catch (error) {
                this.logging.error(
                    error,
                    `Error processing uploaded file: ${error.message}`
                );
            }
        };

        reader.onerror = error => {
            this.logging.error(error, `Error reading uploaded location file`);
        };

        reader.readAsText(locationFile, 'UTF-8');
    }

    showAirspaceDisclaimerDialogue() {
        const disclaimer =
            this.location?.airspaceJurisdiction?.airspaceDisclaimer ??
            this.mapJurisdiction?.airspaceDisclaimer ??
            this.map?.currentlySelectedJurisdiction?.airspaceDisclaimer;
        const airspaceJurisdictionId =
            this.location?.airspaceJurisdiction?.id ??
            this.mapJurisdiction?.id ??
            this.map?.currentlySelectedJurisdictionId;
        if (
            airspaceJurisdictionId == null ||
            disclaimer == null ||
            disclaimer.length == 0
        ) {
            this.showAirspaceDisclaimer = false;
            this.changeDetector.markForCheck();
            return;
        }
        this.mapJurisdiction =
            this.mapJurisdiction ?? this.location?.airspaceJurisdiction;
        this.preferencesService
            .findPreferencesAsOption(AIRSPACE_DISCLAIMER, null)
            .pipe(
                subjectMap(p =>
                    getOrElse(() => ({
                        [airspaceJurisdictionId]: moment(new Date())
                            .subtract(1, 'day')
                            .toDate()
                    }))(p)
                ),
                takeUntil(this.ngUnsubscribe$),
                take(1)
            )
            .subscribe({
                next: preferences => {
                    this.airspaceDisclaimerDates = preferences;
                    if (
                        preferences == null ||
                        preferences[airspaceJurisdictionId] == null
                    ) {
                        this.showAirspaceDisclaimer = true;
                        this.changeDetector.markForCheck();
                        return;
                    }
                    const today = moment(new Date()).format('YYYY-MM-DD');
                    const lastViewed = moment(
                        preferences[airspaceJurisdictionId]
                    ).format('YYYY-MM-DD');
                    // Only show the disclaimer once per account
                    this.showAirspaceDisclaimer =
                        moment(lastViewed).isBefore(today);
                    this.changeDetector.markForCheck();
                },
                error: (error: FlyFreelyError) => {
                    this.logging.error(error);
                }
            });
    }

    onAirspaceDisclaimerAcknowledged() {
        this.showAirspaceDisclaimer = false;
        const airspaceJurisdictionId =
            this.location?.airspaceJurisdiction?.id ??
            this.mapJurisdiction?.id ??
            this.map?.currentlySelectedJurisdictionId;
        this.preferencesService
            .updatePreferences(AIRSPACE_DISCLAIMER, null, {
                ...this.airspaceDisclaimerDates,
                [airspaceJurisdictionId]: new Date()
            })
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(() => DO_NOTHING)
            .add(this.workTracker.createTracker());
    }

    save() {
        if (this.hasInvalidFeatures()) {
            this.showInvalidFeaturesConfirmation(() => this.performSave());
            return;
        }
        this.performSave();
    }

    private showInvalidFeaturesConfirmation(onConfirm: () => void) {
        const invalidCount = this.invalidFeatures.size;
        const message = `This location contains ${invalidCount} invalid feature${
            invalidCount > 1 ? 's' : ''
        }. Invalid features may cause issues with mission planning and execution. Are you sure you want to save?`;
        this.commonDialoguesService.showConfirmationDialogue(
            'Invalid Features Detected',
            message,
            'Save Anyway',
            () => {
                onConfirm();
                return Promise.resolve();
            }
        );
    }

    private performSave() {
        if (this.isAddingFeature) {
            this.logging.warn(
                `Please finish adding the current feature before saving.`
            );
            return;
        }
        if (this.isEditing) {
            this.map.doneEditing();
            this.refreshValidity();
            if (this.hasRequiredFeatures) {
                return this.save();
            } else {
                // Under normal use this toast should never fire, it's merely a fallback for in case.
                this.logging.error(
                    null,
                    `Location template does not meet all saving requirements. Please ensure there is a flight area and location name and try again.`
                );
                return;
            }
        }

        if (this.manualSave) {
            this.featuresChange.emit(this.featureGroups);
            this.formGroup.markAsPristine();
            this.hasChanged = false;
            this.modal.hide();
            return;
        }

        // Filter feature groups to all flight areas and corridors (offset flight areas)
        // Escape function if no required groups exist
        const requiredFeatureGroups = this.featureGroups.filter(g =>
            g.existingFeatures.reduce(
                (acc, f) =>
                    acc ||
                    ((f.categoryId === 'FLIGHT_AREA' ||
                        f.categoryId === 'OFFSET_FLIGHT_AREA') &&
                        f.geom != null),
                false
            )
        );
        if (requiredFeatureGroups.length == 0) {
            return;
        }

        // Find if the required feature groups contain a polygon, extracting any polygons from the corridors
        // Escape if the feature is not a polygon, or if the corridor doesn't contain a polygon
        let polygon;
        if (
            requiredFeatureGroups[0].existingFeatures[0].geom.type ===
            'GeometryCollection'
        ) {
            // Extract polygon from geometry collection, or exit function if there is no polygon in collection
            const collection = requiredFeatureGroups[0].existingFeatures[0]
                .geom as GeometryCollection;
            polygon = collection.geometries.find(g => g.type === 'Polygon');
        }
        const geom =
            polygon ?? requiredFeatureGroups[0].existingFeatures[0].geom;
        if (geom.type !== 'Polygon') {
            return;
        }

        const airspaceValue = this.airspaceValue$.getValue();
        const outputMissionValues: MissionAirspaceParameters = {
            id: this.missionValues?.id,
            date: airspaceValue?.airspaceDate,
            duration: airspaceValue?.airspaceDuration,
            rulesetIdentifier: airspaceValue?.airspaceRuleset?.identifier,
            height: airspaceValue?.airspaceHeight
        };

        const newCopy =
            this.location != null &&
            this.location.id != null &&
            !this.location.availableActions.canEdit;

        const name = this.formGroup.value.name;

        const features = this.getFeatures();

        if (this.locationId != null && !newCopy) {
            const updateCommand: UpdateLocationCommand = {
                name,
                features,
                derivedFromId: this.location?.derivedFromId
            };
            this.locationService
                .updateLocation(this.locationId, updateCommand)
                .pipe(takeUntil(this.ngUnsubscribe$))
                .subscribe({
                    next: result => {
                        this.hasChanged = false;
                        this.formGroup.markAsPristine();
                        this.logging.success(
                            `Location updated${
                                this.missionValues != null
                                    ? ' & mission details updated'
                                    : ''
                            }`
                        );
                        this.change.emit(result);
                        if (this.missionValues != null) {
                            this.updatedMissionValues.emit(outputMissionValues);
                        }
                        this.modal.hide();
                    },
                    error: (error: FlyFreelyError) => {
                        this.logging.error(
                            error,
                            `Error while updating location: ${error.message}`
                        );
                    }
                })
                .add(this.workTracker.createTracker());
        } else {
            const createCommand: CreateLocationCommand = {
                name,
                features,
                organisationId: this.organisationId,
                type: this.type,
                derivedFromId:
                    this.defaultValues?.id ??
                    this.defaultValues?.derivedFromId ??
                    null
            };
            this.locationService
                .createLocation(createCommand)
                .pipe(takeUntil(this.ngUnsubscribe$))
                .subscribe({
                    next: result => {
                        this.hasChanged = false;
                        this.formGroup.markAsPristine();
                        this.logging.success(
                            `Location created${
                                this.missionValues != null
                                    ? ' & mission details updated'
                                    : ''
                            }`
                        );
                        this.change.emit(result);
                        if (this.missionValues != null) {
                            this.updatedMissionValues.emit(outputMissionValues);
                        }
                        this.modal.hide();
                    },
                    error: (error: FlyFreelyError) => {
                        this.logging.error(
                            error,
                            `Error while saving location: ${error.message}`
                        );
                    }
                })
                .add(this.workTracker.createTracker());
        }
    }

    createMission() {
        if (this.featureGroups[0].existingFeatures.length === 0) {
            return;
        }
        const geom = this.featureGroups[0].existingFeatures[0].geom;
        if (geom.type !== 'Polygon') {
            return;
        }

        const value = this.formGroup.value;

        const name = value.name;

        const features = this.getFeatures();

        const createCommand: CreateLocationCommand = {
            name,
            features,
            organisationId: this.organisationId,
            type: CreateLocationCommand.Type.MISSION,
            derivedFromId:
                this.defaultValues?.id ??
                this.defaultValues?.derivedFromId ??
                null
        };

        this.locationEditService.createMission(
            this.organisationId,
            this.airspaceValue$.getValue(),
            createCommand
        );
    }

    private getFeatures(): LocationFeatureDto[] {
        return this.featureGroups.reduce(
            (acc, fg) =>
                acc.concat(
                    fg.existingFeatures.reduce(
                        (acc2, f) =>
                            acc2.concat({
                                name: f.name,
                                geometry: f.geom,
                                type: f.categoryId,
                                properties: f.properties
                            }),
                        []
                    )
                ),
            /* fg.existingFeatures.find(
                f => f.geom.type === 'GeometryCollection'
            ) == null
                ? acc.concat(
                      fg.existingFeatures.reduce(
                          (acc2, f) =>
                              acc2.concat({
                                  name: f.name,
                                  geometry: f.geom,
                                  type: f.categoryId,
                                  properties: f.properties
                              }),
                          []
                      )
                  )
                : acc.concat(
                      fg.existingFeatures.reduce(
                          (acc2, f) =>
                              f.geom.type !== 'GeometryCollection'
                                  ? acc2.concat({
                                        name: f.name,
                                        geometry: f.geom,
                                        type: f.categoryId,
                                        properties: f.properties
                                    })
                                  : acc2.concat(
                                        (<GeometryCollection>(
                                            f.geom
                                        )).geometries.map(geom => ({
                                            name: f.name,
                                            geometry: geom,
                                            type: f.categoryId,
                                            properties: f.properties
                                        }))
                                    ),
                          []
                      )
                  ), */
            []
        );
    }

    onViewportChange(viewport: MapViewport) {
        // Only update the map center object if it's needed to limit API calls.
        // Have this logic first so the initial map center is ignored while the map loads
        if (this.timeZoneSource === 'MAP_CENTER') {
            this.mapCenter$.next(viewport);
        }
        // Switch the time zone source from the organisation default to the map center.
        // This will happen once the map has rendered, but due to the logic order in this function,
        // the first emit of the map center to the handlers will only happen once the user scrolls the map.
        if (
            this.timeZoneSource === 'ORGANISATION' &&
            this.map.areMapFeaturesReady
        ) {
            this.timeZoneSource = 'MAP_CENTER';
        }
    }

    addNewFeature(featureGroup: FeatureGroup) {
        this.cancelAddingFeature();

        this.isAddingFeature = true;
        const newFeature: MapFeature = {
            id: this.nextId++,
            name: this.isExistingFeaturesValid(featureGroup)
                ? this.numerateNameIfExist(
                      featureGroup.categories[0].name,
                      featureGroup.existingFeatures
                  )
                : featureGroup.categories[0].name,
            categoryId: featureGroup.categories[0].id,
            geom: {
                // @ts-ignore - bad type sensing
                type: featureGroup.type
            }
        };
        this.selectedFeature = {
            groupId: featureGroup.id,
            feature: newFeature
        };
        this.addedFeature = this.selectedFeature;

        this.featureGroups = this.featureGroups.map(g =>
            g.id === featureGroup.id
                ? {
                      ...g,
                      existingFeatures: g.existingFeatures.concat(newFeature)
                  }
                : g
        );

        this.map.editFeature({
            groupId: featureGroup.id,
            feature: newFeature
        });
    }

    numerateNameIfExist(name: string, existingFeatures: MapFeature[]) {
        const existNames = existingFeatures.map(e => e.name);
        const baseNamePattern = new RegExp(`^${name}( \\d+)?$`);

        // Filter existing names that match the base name pattern
        let matchingNames = existNames.filter(e => baseNamePattern.test(e));
        if (matchingNames.length > 0) {
            // When adding a second feature and the enumerations are added
            // make original feature starting at 1.
            if (matchingNames.length === 1 && matchingNames[0] === name) {
                const newDefault = `${name} 1`;
                existingFeatures[0].name = newDefault;
                matchingNames = [newDefault];
            }
            // Get the highest number suffix from matching names
            const numbers = matchingNames.map(n => {
                const match = n.match(/\d+$/);
                return match ? parseInt(match[0], 10) : 0;
            });
            const maxNumber = Math.max(...numbers);

            // Numerate name
            return `${name} ${maxNumber + 1}`;
        }
        return name;
    }

    isExistingFeaturesValid(featureGroup: FeatureGroup | null) {
        return (
            featureGroup != null && featureGroup.existingFeatures.length !== 0
        );
    }

    cancelAddingFeature() {
        if (this.isAddingFeature && this.selectedFeature != null) {
            // Cancel the existing add first
            this.featureGroups = this.featureGroups.map(g =>
                g.id === this.selectedFeature.groupId
                    ? {
                          ...g,
                          existingFeatures: g.existingFeatures.filter(
                              f => f.id !== this.selectedFeature.feature.id
                          )
                      }
                    : g
            );
        }
        this.validateFeatures();
    }

    editFeature(featureGroup: FeatureGroup, feature: MapFeature) {
        this.editFeatureId = <number>feature.id;
        this.map.editFeature({
            feature,
            groupId: featureGroup.id
        });
    }

    deleteFeature(featureGroup: FeatureGroup, feature: MapFeature) {
        this.map.doneEditing();

        this.featureGroups = this.featureGroups.map(fg =>
            fg.id !== featureGroup.id
                ? fg
                : {
                      ...fg,
                      existingFeatures: fg.existingFeatures.filter(
                          f => f.id !== feature.id
                      )
                  }
        );
        this.validateFeatures();
    }

    highlightFeature(featureGroup: FeatureGroup, highlightFeatureId: number) {
        this.map.highlightFeature(featureGroup.id, highlightFeatureId);
    }

    unhighlightFeature(featureGroup: FeatureGroup, highlightFeatureId: number) {
        this.map.unhighlightFeature(featureGroup.id, highlightFeatureId);
    }

    private setSelectedFeature(feature: FeatureAndGroup) {
        this.selectedFeature = feature;
        this.selectedFeatureGroup = feature
            ? this.featureGroups.find(fg => fg.id === feature.groupId)
            : null;
    }

    private setFeatureName(feature: FeatureAndGroup) {
        if (!!feature && this.isSelectedFeatureGroupValid()) {
            const categories = this.selectedFeatureGroup.categories;
            const presetCategoryNames = categories.map(
                category => category.name
            );
            const isFeatureNameDefault =
                presetCategoryNames.includes(feature.feature.name) ||
                presetCategoryNames.some(presetCategoryName =>
                    new RegExp(`^${presetCategoryName}( \\d+)?$`).test(
                        feature.feature.name
                    )
                );
            const featureGroup = this.featureGroups.find(
                fg => fg.id === feature.groupId
            );
            if (isFeatureNameDefault) {
                const newName = categories.find(
                    category => category.id === feature.feature.categoryId
                ).name;
                feature.feature.name =
                    this.isExistingFeaturesValid(featureGroup) &&
                    featureGroup.existingFeatures.filter(
                        ef => ef.id !== feature.feature.id
                    ).length > 0
                        ? this.numerateNameIfExist(
                              newName,
                              featureGroup.existingFeatures.filter(
                                  ef => ef.id !== feature.feature.id
                              )
                          )
                        : newName;
            }
        }
    }

    isSelectedFeatureGroupValid() {
        return (
            !!this.selectedFeatureGroup &&
            !!this.selectedFeatureGroup.categories &&
            Array.isArray(this.selectedFeatureGroup.categories) &&
            this.selectedFeatureGroup.categories.length !== 0
        );
    }

    /**
     * Find the layout details for passing into the draw function to make sure
     * the draw version of the symbols match the view versions.
     */
    private findDrawProperties(feature: FeatureAndGroup): GeoJsonProperties {
        const group = this.featureGroups.find(g => g.id === feature.groupId);
        if (group == null) {
            return undefined;
        }

        if (group.type !== 'Point' || feature.feature.categoryId == null) {
            return undefined;
        }

        const style = group.styles.symbol.find(
            s =>
                s.layout?.['icon-image'] != null &&
                s.filter[0] === '==' &&
                s.filter[2] === feature.feature.categoryId
        );

        return style.layout;
    }

    handleEditOnClose() {
        if (this.isAddingFeature) {
            this.cancelAddingFeature();
            this.map.doneEditing();
            this.onFeatureDelete(this.selectedFeature);
            this.hasChanged = true;
            this.isAddingFeature = false;
        }
        if (this.isEditing) {
            this.map.doneEditing();
            this.hasChanged = true;
        }
    }

    downloadKml() {
        const name = this.formGroup.value.name ?? 'Location';
        this.locationService
            .downloadKmlFromFeatures({ name, features: this.getFeatures() })
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(
                download => {
                    const headers = download.headers;
                    const fileName =
                        getFileName(headers.get('content-disposition')) ??
                        'flyfreely.kml';

                    FileSaver.saveAs(download.body, fileName);
                },
                (error: FlyFreelyError) =>
                    this.logging.error(
                        error,
                        `Error downloading KML file: ${error.message}`
                    )
            )
            .add(this.workTracker.createTracker());
    }

    close() {
        this.handleEditOnClose();
        this.modal.hide();
    }
}
